import React from 'react';
import { Check, X } from 'lucide-react';
import type { AIAssistantPrototypeWidthMode } from '../types';
import styles from '../styles/AssistantWidthMenu.module.css';

export type AssistantWidthMenuMode = AIAssistantPrototypeWidthMode;

export const ASSISTANT_WIDTH_OPTIONS: Array<{ label: string; value: AssistantWidthMenuMode }> = [
  { label: 'Compact', value: 'compact' },
  { label: 'Expanded', value: 'expanded' },
  { label: 'Half Screen', value: 'half' },
  { label: 'Full Screen', value: 'full' },
];

const MENU_GLYPH_CLASS_NAMES: Record<AssistantWidthMenuMode, string> = {
  compact: styles.menuGlyphCompact,
  expanded: styles.menuGlyphExpanded,
  half: styles.menuGlyphHalf,
  full: styles.menuGlyphFull,
};

export const getAssistantWidthModeLabel = (mode: AssistantWidthMenuMode): string => (
  ASSISTANT_WIDTH_OPTIONS.find((option) => option.value === mode)?.label ?? 'Compact'
);

export type AssistantWidthMenuProps = {
  currentMode: AssistantWidthMenuMode;
  activeMode?: AssistantWidthMenuMode | 'closed';
  onModeSelect: (mode: AssistantWidthMenuMode) => void;
  onClose?: () => void;
  menuTestId?: string;
  optionTestIdPrefix?: string;
  closeTestId?: string;
  className?: string;
  align?: 'left' | 'right';
  open?: boolean;
  floating?: boolean;
};

export const AssistantWidthMenu: React.FC<AssistantWidthMenuProps> = ({
  currentMode,
  activeMode = currentMode,
  onModeSelect,
  onClose,
  menuTestId,
  optionTestIdPrefix,
  closeTestId,
  className,
  align = 'right',
  open = true,
  floating = false,
}) => (
  <div
    className={[
      styles.menu,
      align === 'left' ? styles.menuAlignLeft : styles.menuAlignRight,
      open ? styles.menuOpen : '',
      floating ? styles.menuFloating : '',
      className ?? '',
    ].filter(Boolean).join(' ')}
    data-testid={menuTestId}
    role="menu"
    aria-label="Assistant display options"
  >
    {ASSISTANT_WIDTH_OPTIONS.map((option) => {
      const active = option.value === activeMode;
      return (
        <button
          key={option.value}
          type="button"
          className={`${styles.menuItem} ${active ? styles.menuItemActive : ''}`}
          onClick={() => onModeSelect(option.value)}
          data-testid={optionTestIdPrefix ? `${optionTestIdPrefix}-${option.value}` : undefined}
          role="menuitemradio"
          aria-checked={active}
        >
          <span
            className={`${styles.menuGlyph} ${MENU_GLYPH_CLASS_NAMES[option.value]}`}
            aria-hidden="true"
          />
          <span className={styles.menuLabel}>{option.label}</span>
          {active ? <Check className={styles.menuCheck} /> : null}
        </button>
      );
    })}
    {onClose ? (
      <>
        <div className={styles.menuDivider} />
        {(() => {
          const active = activeMode === 'closed';
          return (
            <button
              type="button"
              className={`${styles.menuItem} ${active ? styles.menuItemActive : ''}`}
              onClick={onClose}
              data-testid={closeTestId}
              role="menuitemradio"
              aria-checked={active}
            >
              <X className={styles.menuCloseIcon} />
              <span className={styles.menuLabel}>Close</span>
              {active ? <Check className={styles.menuCheck} /> : null}
            </button>
          );
        })()}
      </>
    ) : null}
  </div>
);
